/**
 * Excel Skill Registry — plumbing for the `get_skill` tool.
 *
 * This module owns:
 * - The skill type system and catalog (what skills exist, their enum descriptions)
 * - The `get_skill` tool definition and executor
 * - Content routing (maps a skill id to its content module)
 *
 * Skill content lives in separate files under `./skills/`. To add a new skill:
 * 1. Create `./skills/<id>.ts` exporting a content string constant.
 * 2. Add the id to `SkillId`, add a catalog entry, and add a switch case in `getSkillContent`.
 */

import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import { DCF_SKILL_CONTENT } from "./skills/dcf";
import { LBO_SKILL_CONTENT } from "./skills/lbo";
import { TRADING_COMPS_SKILL_CONTENT } from "./skills/tradingComps";
import { PRECEDENT_TRANSACTIONS_SKILL_CONTENT } from "./skills/precedentTransactions";
import { OPERATING_MODEL_SKILL_CONTENT } from "./skills/operatingModel";
import { THREE_STATEMENT_SKILL_CONTENT } from "./skills/threeStatement";
import { DEBT_SCHEDULE_SKILL_CONTENT } from "./skills/debtSchedule";
import { RETURNS_ANALYSIS_SKILL_CONTENT } from "./skills/returnsAnalysis";
import { SENSITIVITY_SCENARIO_SKILL_CONTENT } from "./skills/sensitivityScenario";
import { EV_BRIDGE_SKILL_CONTENT } from "./skills/evBridge";
import {
  buildFormattingStyleSkillContent,
  FORMATTING_STYLE_SKILL_CONTENT,
  type FormattingStyleOverride,
} from "./skills/formattingStyle";
import { STRUCTURE_LAYOUT_SKILL_CONTENT } from "./skills/structureLayout";
import { DATA_FORMULA_SKILL_CONTENT } from "./skills/dataFormula";
import { QA_VALIDATION_SKILL_CONTENT } from "./skills/qaValidation";
import { diag } from "./aiDiag";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Union of all registered skill identifiers. Extend this as new skills are added. */
export type SkillId = "dcf" | "lbo" | "trading_comps" | "precedent_transactions" | "operating_model" | "three_statement" | "debt_schedule" | "returns_analysis" | "sensitivity_scenario" | "ev_bridge" | "formatting_style" | "structure_layout" | "data_formula" | "qa_validation";

/**
 * Broad category of what a skill does in the modelling pipeline.
 *
 * - `"output"` — builds a complete, named deliverable (DCF, LBO, comps, etc.).
 * - `"tool"` — a reusable component that output skills compose (operating model,
 *    debt schedule, EV bridge, etc.).
 * - `"hygiene"` — always-on discipline that applies to every build and edit
 *    (formatting, structure, data layer, QA).
 */
export type SkillType = "output" | "tool" | "hygiene";

/**
 * How a skill participates in the overall model build.
 *
 * - `"standalone"` — the skill owns its deliverable end-to-end and is the
 *    top-level entry point the planner selects.
 * - `"shared"` — the skill is a reusable building block that multiple output
 *    skills may compose into their workflow.
 * - `"global"` — the skill is always active ("pushed, not pulled") and applies
 *    its discipline to every build and edit automatically.
 */
export type SkillSharing = "standalone" | "shared" | "global";

/**
 * How a skill's content reaches the model.
 *
 * - `"pull"` — the planner explicitly fetches the skill via `get_skill` when the
 *    request warrants it (all output and tool skills).
 * - `"push"` — the skill is always-on: it should be injected into every build and
 *    edit automatically, without the planner having to ask for it (the hygiene
 *    skills, which describe themselves as "pushed, not pulled").
 *
 * Push behaviour is wired in `excelAIService`: push skills are injected into
 * the planner prompt directly and merged into executor/verifier skill context
 * via `withPushSkills(...)`.
 */
export type SkillDelivery = "push" | "pull";

/** A catalog entry describing one skill — surfaced in the `get_skill` tool enum. */
export interface SkillCatalogEntry {
  /** Stable identifier used in tool calls and plan metadata. */
  id: SkillId;
  /** Human-readable display name. */
  name: string;
  /** One-line description the planner reads to decide whether to fetch this skill. */
  description: string;
  /** Broad category: output (deliverable), tool (shared component), or hygiene (always-on). */
  skillType: SkillType;
  /** Sharing semantics: standalone (top-level), shared (composed), or global (pushed). */
  sharing: SkillSharing;
  /**
   * Delivery model: `"pull"` skills are fetched on demand by the planner;
   * `"push"` skills are always-on and (once wired) apply to every build automatically.
   * Push skills are exactly the hygiene/global skills.
   */
  delivery: SkillDelivery;
  /**
   * Skill ids that this skill commonly composes or depends on.
   * Empty array means the skill is self-contained at the framework level
   * (hygiene skills and leaf tools typically have no declared dependencies).
   */
  dependencies: readonly SkillId[];
}

// ---------------------------------------------------------------------------
// Catalog — the enum descriptions the planner sees in the tool definition
// ---------------------------------------------------------------------------

/**
 * Registry of available skills. Each entry's `description` is the selection
 * surface: the planner reads it (via the `get_skill` tool enum) to decide
 * whether a request warrants fetching that skill's full content.
 */
export const SKILL_CATALOG: readonly SkillCatalogEntry[] = [
  {
    id: "dcf",
    name: "DCF — Discounted Cash Flow",
    description:
      "Build, extend, or refresh a discounted-cash-flow valuation in Excel — forecast unlevered " +
      "free cash flow, discount at WACC, add a terminal value, and bridge enterprise value to equity " +
      "value per share. Use whenever the request is intrinsic/fundamental/cash-flow-based valuation, " +
      "a DCF, a DCF-derived target price, or adding/updating a DCF in an existing model — even if " +
      "the word 'DCF' isn't used. Do not use for trading comps, precedent transactions, or LBO " +
      "returns; call those skills instead.",
    skillType: "output",
    sharing: "standalone",
    delivery: "pull",
    dependencies: ["operating_model", "ev_bridge"],
  },
  {
    id: "lbo",
    name: "LBO — Leveraged Buyout",
    description:
      "Build, extend, or refresh a leveraged-buyout model in Excel — size the acquisition financing, " +
      "sweep the business's cash through the debt waterfall over the hold, exit on a multiple, and " +
      "compute the sponsor's equity returns (IRR and MOIC). Use whenever the request is a buyout / " +
      "take-private / sponsor or PE returns analysis, an LBO, an ability-to-pay or max-leverage read, " +
      "a returns (value-creation) bridge, or adding/updating an LBO in an existing model — even if " +
      "the word 'LBO' isn't used. Do not use for intrinsic DCF value, trading comps, or precedent " +
      "transactions; call those skills instead.",
    skillType: "output",
    sharing: "standalone",
    delivery: "pull",
    dependencies: ["operating_model", "three_statement", "debt_schedule", "returns_analysis"],
  },
  {
    id: "trading_comps",
    name: "Trading Comps — Comparable Companies",
    description:
      "Build a trading-comparables ('comps') analysis in Excel — assemble a defensible peer set, " +
      "compute calendarised like-for-like multiples, summarise them (median / quartiles / high-low), " +
      "and apply them to the subject for an implied valuation range. Use whenever the request is " +
      "relative valuation off public peers — 'comps', 'trading comparables', 'how does X trade vs " +
      "peers', 'what multiple is X worth', peer screening, or the comps leg of a football field — " +
      "even if the word 'comps' isn't used. Do not use for precedent-transaction (deal) multiples " +
      "with control premia (call Precedent Transactions skills), intrinsic DCF value (DCF skills), or " +
      "peer forecasting (Operating Model skills); call those instead.",
    skillType: "output",
    sharing: "standalone",
    delivery: "pull",
    dependencies: ["ev_bridge"],
  },
  {
    id: "precedent_transactions",
    name: "Precedent Transactions — Deal Comps",
    description:
      "Build a precedent-transactions ('deal comps') analysis in Excel — screen comparable past M&A " +
      "deals, compute the multiples paid and the premia to the unaffected price, summarise them, and " +
      "apply them to the subject for an implied takeover valuation. Use whenever the request is " +
      "relative valuation off past deals — 'precedents', 'transaction comps', 'deal comps', 'what " +
      "would X go for', 'premium analysis', or the precedents leg of a football field — even if the " +
      "word 'precedents' isn't used. Do not use for trading (public-peer) multiples without control " +
      "premia (call Trading Comps skills), intrinsic DCF value (DCF skills), or merger " +
      "accretion/dilution; call those skills instead.",
    skillType: "output",
    sharing: "standalone",
    delivery: "pull",
    dependencies: ["ev_bridge"],
  },
  {
    id: "operating_model",
    name: "Operating Model — Bottom-Up Forecast",
    description:
      "Build a bottom-up operating forecast in Excel — project revenue, costs and margins from " +
      "sector-specific operating drivers, anchored to normalised historicals and guidance, down to " +
      "EBITDA and EBIT. Use whenever a forecast is needed — 'build a model for X', 'project the " +
      "next five years', 'forecast revenue by segment', 'model the P&L' — or as the forecast engine " +
      "feeding a DCF, LBO, merger or credit model, even if the word 'model' isn't used. Do not use " +
      "for the full balance-sheet/cash-flow integration (call Three Statement Model skills) or for " +
      "valuation (DCF skills, Trading Comps skills, LBO skills); call those instead.",
    skillType: "tool",
    sharing: "shared",
    delivery: "pull",
    dependencies: [],
  },
  {
    id: "three_statement",
    name: "Three-Statement Model — Integrated Financial Model",
    description:
      "Build a fully integrated three-statement model in Excel — income statement, balance sheet " +
      "and cash flow wired together so the balance sheet balances every period, cash ties out, and " +
      "the debt-and-interest circularity resolves. Use whenever a request needs an integrated or " +
      "balancing model — 'build a 3-statement model', 'a fully linked model', 'a model that " +
      "balances' — or as the integrated core beneath an LBO, merger or credit model, even if the " +
      "phrase isn't used. Do not use for the operating P&L forecast alone (call Operating Model " +
      "skills), the debt rollforward alone (Debt Schedule skills), or valuation (DCF skills, " +
      "Trading Comps skills, LBO skills); call those instead.",
    skillType: "tool",
    sharing: "shared",
    delivery: "pull",
    dependencies: ["operating_model", "debt_schedule"],
  },
  {
    id: "debt_schedule",
    name: "Debt Schedule — Tranche Rollforward & Sweep",
    description:
      "Build, extend, or refresh a debt schedule in Excel — roll each tranche of the capital " +
      "structure forward, compute cash and PIK interest off the resolved balance, apply mandatory " +
      "amortisation and the cash-flow sweep by seniority, draw and repay the revolver, and resolve " +
      "the interest-balance-cash circularity. Use whenever the request involves a debt rollforward, " +
      "a cash sweep / deleveraging schedule, tranche interest, a revolver, mandatory amortisation, " +
      "PIK accretion, or the debt block inside an LBO, credit or integrated model — even if the " +
      "phrase 'debt schedule' isn't used. Do not use for the operating forecast (call Operating " +
      "Model skills), equity returns (Returns Analysis skills), or the full three-statement " +
      "integration (Three Statement Model skills); call those instead.",
    skillType: "tool",
    sharing: "shared",
    delivery: "pull",
    dependencies: [],
  },
  {
    id: "returns_analysis",
    name: "Returns Analysis — IRR, MOIC & Attribution",
    description:
      "Compute investment returns in Excel from a dated cash-flow stream — IRR (XIRR on dated " +
      "flows), MOIC / multiple of money, cash-on-cash, and the value-creation attribution that " +
      "explains them — and back-solve any one input (entry price, leverage, exit multiple) for a " +
      "target return. Use whenever the request is a returns calc — IRR, MOIC, cash-on-cash, money " +
      "multiple, a returns or value-creation bridge, 'what return does this make', or an " +
      "ability-to-pay / max-leverage / required-return back-solve — even if the term isn't used, " +
      "and as the returns engine inside an LBO, buy-side or project model. Do not use for the " +
      "equity value itself (the DCF or LBO skill handles the bridge), the debt rollforward (Debt Schedule " +
      "skills), or the operating forecast (Operating Model skills); call those instead.",
    skillType: "tool",
    sharing: "shared",
    delivery: "pull",
    dependencies: [],
  },
  {
    id: "sensitivity_scenario",
    name: "Sensitivity & Scenario Modelling",
    description:
      "Build sensitivity tables and scenario switches in Excel onto an existing model — one- and " +
      "two-way data tables that flex the inputs that move the answer, named operating and financing " +
      "cases driven from a control sheet, and the toggles that switch between them — presented so " +
      "the range reads at a glance. Use whenever the request is a sensitivity / data table, a " +
      "tornado, a scenario or case (base / management / downside / upside), an operating or " +
      "financing case, a 'what if' / 'how sensitive' / 'flex the assumptions' ask, or a switchable " +
      "case structure — and as the case-and-sensitivity layer inside any output model. Do not use " +
      "for the underlying analysis itself (call DCF, LBO, Operating Model skills, etc.) or for the " +
      "returns metrics it flexes (Returns Analysis skills); call those instead.",
    skillType: "tool",
    sharing: "shared",
    delivery: "pull",
    dependencies: [],
  },
  {
    id: "ev_bridge",
    name: "EV to Equity Bridge",
    description:
      "Build the bridge between enterprise value and equity value in Excel — net debt, preferred, " +
      "minority interest, associates and other claims — in either direction, and convert to value " +
      "per share on a fully diluted (treasury-stock) share count. Use whenever a valuation needs to " +
      "cross between EV and equity — turning a DCF, comps or LBO EV into equity value or a price " +
      "per share, building EV from market cap or an offer price, or computing a diluted share count " +
      "— even if the word 'bridge' isn't used. Do not use for the valuation itself (call DCF, " +
      "Trading Comps, LBO skills, etc.), the debt rollforward that produces net debt (Debt Schedule " +
      "skills), or the returns it feeds (Returns Analysis skills); call those instead.",
    skillType: "tool",
    sharing: "shared",
    delivery: "pull",
    dependencies: [],
  },
  {
    id: "formatting_style",
    name: "Model Formatting",
    description:
      "The always-on house-style layer for every model in Excel — palette, fonts, number formats, " +
      "the colours that render the input/formula/link classification, table and chart styling, the " +
      "source line and footnotes, the sensitivity heatmap ramp, and white-labelling to FactSet. " +
      "Its central judgment is template-theme precedence: when a bank template is supplied, its " +
      "theme overrides house defaults, which override skill defaults. This skill is pushed, not " +
      "pulled: it applies to every build and every edit automatically — output and tool skills do " +
      "not invoke it for a feature, they defer their look-and-feel to it. Not a modelling skill; " +
      "it governs how a model looks, not what it computes or where things sit (that is Model " +
      "Structuring & Layout skills).",
    skillType: "hygiene",
    sharing: "global",
    delivery: "push",
    dependencies: [],
  },
  {
    id: "structure_layout",
    name: "Model Structuring & Layout",
    description:
      "The always-on layout layer for every model in Excel — how a tab is laid out, how a workbook " +
      "is organised into tabs, where inputs / builds / outputs / checks sit, named ranges, the " +
      "generic template mechanics (mapping data in, adapting rows, removing unused placeholders), " +
      "and reading an unfamiliar model's structure before editing it. It carries a visual layout " +
      "pattern so a model built on a blank canvas is spatially coherent, not just conceptually " +
      "correct. This skill is pushed, not pulled: it applies to every build and every edit " +
      "automatically — output and tool skills do not invoke it for a feature, they defer their " +
      "structure and layout to it. Not a modelling skill; it governs where things sit, not what " +
      "they compute (Data & Formula skills) or how they look (Model Formatting skills).",
    skillType: "hygiene",
    sharing: "global",
    delivery: "push",
    dependencies: [],
  },
  {
    id: "data_formula",
    name: "Data & Formula",
    description:
      "The always-on discipline for how every input enters a model and becomes a formula in Excel " +
      "— when to hardcode a FactSet pull vs link its parameters, when to use an FDS formula vs an " +
      "Excel build (and how to reconcile the two), units and scaling, source comments, " +
      "normalisation, and the input/formula/link classification. This skill is pushed, not pulled: " +
      "it applies to every build and every edit automatically, regardless of what was asked — " +
      "output and tool skills do not invoke it for a feature, they defer their data and formula " +
      "discipline to it. Not a valuation or modelling skill; it governs the data layer underneath " +
      "all of them.",
    skillType: "hygiene",
    sharing: "global",
    delivery: "push",
    dependencies: [],
  },
  {
    id: "qa_validation",
    name: "QA Validation",
    description:
      "The always-on check layer for every model in Excel — structural integrity (no errors, " +
      "consistent formulas, intact links), financial articulation (balance sheet balances, cash " +
      "ties out, EV-to-equity ties, reconciliations), sanity (high/low, outliers, plausible rates), " +
      "and edit integrity (only intended cells moved, links preserved). It runs continuously while " +
      "building and editing, and as a full battery before output. This skill is pushed, not pulled: " +
      "it applies to every build and every edit automatically — output and tool skills do not " +
      "invoke it for a feature, they defer their universal checks to it and it always runs. It " +
      "owns the universal checks and the shared result format; each skill keeps its own specific checks.",
    skillType: "hygiene",
    sharing: "global",
    delivery: "push",
    dependencies: [],
  },
] as const;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Request-scoped state for the `get_skill` tool.
 *
 * This is passed through `ToolCallContext` for a single LLM tool loop so
 * concurrent planner runs cannot reset or append each other's skill ids.
 */
export interface SkillToolContext {
  /** Skill ids successfully fetched by `get_skill` in this request. */
  fetchedSkillIds: string[];
}

/**
 * Create a fresh request-scoped skill tool context.
 *
 * @returns Empty skill context for one planner/executor tool loop.
 */
export function createSkillToolContext(): SkillToolContext {
  return { fetchedSkillIds: [] };
}

/**
 * Returns a deduplicated list of skill ids fetched in this request.
 *
 * @param context - Request-scoped skill tool context.
 * @returns Array of skill ids that were successfully fetched via `get_skill`.
 */
export function getFetchedSkills(context: SkillToolContext): string[] {
  return context.fetchedSkillIds.filter((id, i, arr) => arr.indexOf(id) === i);
}

/**
 * Returns the full prompt content for the given skill.
 *
 * @param skillId - The identifier of the skill to retrieve (must match a registered `SkillId`).
 * @returns The complete skill prompt text as a string.
 * @throws {Error} If the skillId does not match any registered skill.
 */
export function getSkillContent(skillId: string): string {
  switch (skillId) {
    case "dcf":
      return DCF_SKILL_CONTENT;
    case "lbo":
      return LBO_SKILL_CONTENT;
    case "trading_comps":
      return TRADING_COMPS_SKILL_CONTENT;
    case "precedent_transactions":
      return PRECEDENT_TRANSACTIONS_SKILL_CONTENT;
    case "operating_model":
      return OPERATING_MODEL_SKILL_CONTENT;
    case "three_statement":
      return THREE_STATEMENT_SKILL_CONTENT;
    case "debt_schedule":
      return DEBT_SCHEDULE_SKILL_CONTENT;
    case "returns_analysis":
      return RETURNS_ANALYSIS_SKILL_CONTENT;
    case "sensitivity_scenario":
      return SENSITIVITY_SCENARIO_SKILL_CONTENT;
    case "ev_bridge":
      return EV_BRIDGE_SKILL_CONTENT;
    case "formatting_style":
      return FORMATTING_STYLE_SKILL_CONTENT;
    case "structure_layout":
      return STRUCTURE_LAYOUT_SKILL_CONTENT;
    case "data_formula":
      return DATA_FORMULA_SKILL_CONTENT;
    case "qa_validation":
      return QA_VALIDATION_SKILL_CONTENT;
    default:
      throw new Error(
        `Unknown skill id: "${skillId}". Available: ${SKILL_CATALOG.map((s) => s.id).join(", ")}.`,
      );
  }
}

/**
 * Build the `get_skill` tool definition for the LLM tool-calling interface.
 * The enum descriptions tell the model what each skill covers so it can
 * decide whether to fetch the full content. Metadata (type/sharing) is
 * included so the planner understands the skill hierarchy.
 *
 * @returns A `ChartCapabilityToolDef` describing the tool's name, description,
 *   and JSON schema for the LLM tool-calling interface.
 */
export function buildGetSkillTool(): ChartCapabilityToolDef {
  // Only pull skills are selectable. Push (hygiene/global) skills are
  // always-on and auto-injected into every prompt, so the planner must
  // not (and need not) fetch them.
  const pullSkills = SKILL_CATALOG.filter((s) => s.delivery === "pull");
  const skillLines = pullSkills.map(
    (s) =>
      `- "${s.id}" [${s.skillType}/${s.sharing}]` +
      (s.dependencies.length > 0 ? ` (depends on: ${s.dependencies.join(", ")})` : "") +
      `: ${s.description}`,
  );
  return {
    name: "get_skill",
    description:
      "Fetch domain-specific modeling guidance before planning. Call this when the request " +
      "involves building or editing a financial model that would benefit from expert knowledge. " +
      "Pass an array of all the top-level skills the request needs in ONE call.\n\n" +
      "Dependencies are handled for you: each skill's dependencies (shown as 'depends on' " +
      "below) are resolved and returned automatically, so you only need to name the top-level " +
      "skill(s) — e.g. requesting 'lbo' also returns 'operating_model', 'debt_schedule', and " +
      "'returns_analysis'. There is no need to call this tool again to chase dependencies.\n\n" +
      "You do NOT need to fetch formatting / structure / data / QA guidance — those are " +
      "always-on and applied automatically.\n\n" +
      "Skill types: output (standalone deliverable), tool (shared component).\n\n" +
      "Available skills:\n" +
      skillLines.join("\n"),
    inputSchema: {
      type: "object",
      properties: {
        skillIds: {
          type: "array",
          items: {
            type: "string",
            enum: pullSkills.map((s) => s.id),
          },
          description:
            "All top-level skills the request needs, in one call. Their dependencies are " +
            "added automatically — you do not need to list them yourself. Options — " +
            pullSkills
              .map(
                (s) =>
                  `${s.id}` +
                  (s.dependencies.length > 0
                    ? ` (auto-adds: ${s.dependencies.join(", ")})`
                    : ""),
              )
              .join(" | "),
        },
      },
      required: ["skillIds"],
    },
  };
}

/**
 * Skill ids that are always-on ("pushed, not pulled") — the hygiene skills.
 * Derived from the catalog so it stays in sync with the `delivery` labels.
 *
 * These are auto-injected into the planner, executor, and verifier prompts on
 * every build/edit (see `buildPushSkillContextBlock` / `withPushSkills`), and
 * are deliberately excluded from the `get_skill` enum so the planner never
 * needs to fetch them.
 */
export const PUSH_SKILL_IDS: readonly SkillId[] = SKILL_CATALOG.filter(
  (s) => s.delivery === "push",
).map((s) => s.id);

/**
 * Whether a skill is delivered by push (always-on) rather than pull (on demand).
 *
 * @param skillId - The skill identifier to check.
 * @returns `true` if the skill is a push/always-on skill, `false` otherwise
 *   (including for unknown ids).
 */
export function isPushSkill(skillId: string): boolean {
  return findCatalogEntry(skillId)?.delivery === "push";
}

/**
 * Look up a catalog entry by its id.
 *
 * @param skillId - The skill identifier to find.
 * @returns The matching `SkillCatalogEntry`, or `undefined` if not found.
 */
function findCatalogEntry(skillId: string): SkillCatalogEntry | undefined {
  return SKILL_CATALOG.find((s) => s.id === skillId);
}

/**
 * Compute the transitive dependency closure for a set of requested skill ids.
 *
 * Walks each skill's `dependencies`, then their dependencies, and so on, so a
 * single request yields the complete set needed to build correctly. Push
 * (always-on) skills are excluded — they are auto-injected and never fetched.
 *
 * @param requestedIds - The skill ids the caller explicitly asked for.
 * @returns Deduplicated ids: the requested ids plus all transitive (pull) deps,
 *   with requested ids kept first and dependencies appended in discovery order.
 */
function resolveSkillClosure(requestedIds: string[]): string[] {
  const resolved: string[] = [];
  const queue = [...requestedIds];
  while (queue.length > 0) {
    const id = queue.shift() as string;
    if (resolved.includes(id)) continue;
    resolved.push(id);
    const entry = findCatalogEntry(id);
    for (const dep of entry?.dependencies ?? []) {
      if (!resolved.includes(dep) && !isPushSkill(dep)) queue.push(dep);
    }
  }
  return resolved;
}

/**
 * Execute the `get_skill` tool call. Accepts one or more skill ids and returns
 * each skill's full content with its catalog metadata.
 *
 * Dependencies are resolved automatically: requesting a skill also returns its
 * transitive dependency skills, so the caller gets the complete picture in a
 * single call and never has to chase dependencies with follow-up calls.
 *
 * Accepts either the array shape `{ skillIds: string[] }` (preferred) or the
 * legacy single shape `{ skillId: string }` for backward compatibility.
 *
 * @param rawInput - The parsed JSON arguments from the LLM's function_call output.
 * @param context - Optional request-scoped tracker used to record successful fetches.
 * @returns A JSON string of shape
 *   `{ skills: [{ skillId, name, skillType, sharing, delivery, dependencies, requested, content }],
 *      errors?: [{ skillId, error }] }`,
 *   or `{ error }` when no valid ids were supplied.
 */
export function executeGetSkillTool(rawInput: unknown, context?: SkillToolContext): string {
  const input = rawInput as { skillIds?: unknown; skillId?: unknown };
  const requestedIds: string[] = Array.isArray(input?.skillIds)
    ? (input.skillIds as unknown[]).filter((x): x is string => typeof x === "string")
    : typeof input?.skillId === "string"
      ? [input.skillId]
      : [];

  if (requestedIds.length === 0) {
    return JSON.stringify({
      error: "Missing required parameter: skillIds (array of skill ids).",
    });
  }

  const resolvedIds = resolveSkillClosure(requestedIds);
  const skills: Array<Record<string, unknown>> = [];
  const errors: Array<{ skillId: string; error: string }> = [];

  for (const id of resolvedIds) {
    try {
      const content = getSkillContent(id);
      const entry = findCatalogEntry(id);
      context?.fetchedSkillIds.push(id);
      skills.push({
        skillId: id,
        name: entry?.name ?? id,
        skillType: entry?.skillType ?? "unknown",
        sharing: entry?.sharing ?? "unknown",
        delivery: entry?.delivery ?? "unknown",
        dependencies: entry?.dependencies ?? [],
        // True if the caller asked for this skill directly; false if it was
        // pulled in automatically as a dependency.
        requested: requestedIds.includes(id),
        content,
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      errors.push({ skillId: id, error: msg });
    }
  }

  diag("skill", "get_skill_executed", {
    requested: requestedIds,
    resolved: resolvedIds,
    fetched: skills.map((s) => s.skillId),
    errors: errors.map((e) => e.skillId),
  });

  return JSON.stringify({
    skills,
    errors: errors.length > 0 ? errors : undefined,
  });
}

/**
 * Build a prompt context block containing the content of all selected skills.
 * Each skill section is prefixed with a metadata header showing its type,
 * sharing semantics, and dependency list so the downstream model understands
 * the skill's role in the pipeline.
 *
 * Returns an empty string if no skills are selected, so it can be safely
 * concatenated without conditional logic.
 *
 * @param skillIds - Array of skill ids to include (typically from `plan.selectedSkills`).
 * @param options - Optional formatting override used only for the formatting skill.
 * @returns A formatted prompt block, or empty string if no valid skills.
 */
export function buildSkillContextBlock(
  skillIds: string[] | undefined,
  options?: { formattingStyleOverride?: FormattingStyleOverride },
): string {
  if (!skillIds || skillIds.length === 0) return "";
  const sections: string[] = [];
  for (const id of skillIds) {
    try {
      const content =
        id === "formatting_style" && options?.formattingStyleOverride
          ? buildFormattingStyleSkillContent(options.formattingStyleOverride)
          : getSkillContent(id);
      const entry = findCatalogEntry(id);
      const meta = entry
        ? `[skill: ${entry.id} | type: ${entry.skillType} | sharing: ${entry.sharing} | delivery: ${entry.delivery}` +
          (entry.dependencies.length > 0
            ? ` | depends: ${entry.dependencies.join(", ")}`
            : "") +
          "]"
        : `[skill: ${id}]`;
      sections.push(`${meta}\n${content}`);
    } catch (error) {
      if (id === "formatting_style" && options?.formattingStyleOverride) {
        throw error;
      }
      // Skip unknown skill ids gracefully
    }
  }
  if (sections.length === 0) return "";
  return `\n\n## SKILL CONTEXT\n\n${sections.join("\n\n")}`;
}

/**
 * Merge the planner-selected pull skills with the always-on push skills into
 * a single deduplicated id list. Selected (pull) skills come first so the
 * specific model guidance leads, with the always-on hygiene skills after.
 *
 * @param selectedSkills - Skill ids the planner explicitly fetched (may be undefined).
 * @returns Deduplicated list of pull + push skill ids.
 */
export function withPushSkills(selectedSkills: string[] | undefined): string[] {
  const ids = [...(selectedSkills ?? []), ...PUSH_SKILL_IDS];
  return ids.filter((id, i, arr) => arr.indexOf(id) === i);
}

/**
 * Build the always-on push skill context block (the hygiene skills).
 *
 * Injected into the planner prompt so the planner reasons with the house
 * formatting / structure / data / QA discipline already in view. The executor
 * and verifier get the same content via `buildSkillContextBlock(withPushSkills(...))`.
 *
 * @returns A formatted prompt block of all push skills, or empty string if none.
 */
export function buildPushSkillContextBlock(): string {
  return buildSkillContextBlock([...PUSH_SKILL_IDS]);
}
